import * as React from 'react';
import { EmptyTimeSeries } from './EmptyTimeSeries';
import { formatDataPointAsMetricNumber } from '../formatting/formatting';
import { getBAN, getIconForLabel, isSingleNumberMetric } from '../utils';
import { Messages } from '../../gen-src/Localize';
import { MetricLegend } from './MetricLegend';
import { MetricsBaseProps } from '../MetricsBaseProps';
import { ResizableDetailTimeSeries } from './DetailTimeSeries';
import { TabStyles } from '@tableau/widgets';
import {
  DateFormatterCollection,
  getDateFormatter,
  formatComparisonTextForDetail,
  ComparisonDelta,
  DataRow,
  MetricBlue,
  computeMetricComparison,
  getDateRange,
  DataTable,
  buildFirstThresholdMeasureAccessor,
  buildFirstThresholdTimestampAccessor,
  ValueAccessor,
  buildMeasureAccessor,
  buildTimestampAccessor,
  shouldRenderFirstThresholdLine,
  getStylePropsForDefaultThresholdLine,
  buildComparisonLineLabelAccessor,
  getLineStyleForDefaultThresholdLine,
  buildColorAccessor,
  MetricsGrey,
  MetricsNeutralColor,
  getStatus,
  getComparisonDateRange,
  getWindowDateRange,
  getFirstWindow,
  DateRange,
  materializeTable,
  getMeasureFormatString,
  DataPointsApiResponse,
  getTimestampGranularity,
  dateTruncFromGranularity,
  DateWindow,
  getFirstWindowOrDefault,
  definitionWithWindow,
} from '@tableau/metrics-engine';
import { SvgIconProps } from '../resources/SvgIcon';
import { FeatureFlags } from '../FeatureFlags';
import { createWindowSwitcherOptions, WindowSwitcher } from './WindowSwitcher';

const { Colors } = TabStyles;

const DEFAULT_HEIGHT = '510px';

/**
 * Props for rendering an interactive MetricDetailView
 *
 * Extends from {@link DataPointsApiResponse}, as each of the three fields from
 * that object are used in rendering.
 *
 * Note: Pass in the parsed response directly from
 * `@tableau/metrics-engine#parseResponse`, do not materialize or filter the
 * `data` beforehand.
 *
 */
export interface MetricDetailViewProps extends DataPointsApiResponse {
  /**
   * Feature flags for controlling behavior under development
   */
  readonly flags: FeatureFlags;
  /**
   * Determines whether the window switcher is interactive and has a disabled style
   * (not whether it is visible or not)
   */
  readonly disabledWindowSwitcher?: boolean;
  /**
   * Size information for layout.
   * By default, the view will expand to fill the width of its container,
   * and height defaults to {@link DEFAULT_HEIGHT}
   */
  readonly size?: {
    readonly width?: string;
    readonly height?: string;
  };
  /**
   * The id to give the timeseries for the WindowSwitcher to use in its `aria-controls`. Should
   * be specified if there is more than one timeseries on the page.
   */
  readonly timeSeriesId?: string;
}

/**
 * The `MetricDetailView` is a fast, interactive timeseries display, designed for quick analysis of historical
 * trends.
 *
 * It displays an Interactive Line Chart with BAN, delta label, and hover tooltip.
 *
 * Responsively sized to fill the width of its container, with a default height of {@link DEFAULT_HEIGHT}
 */
export const MetricDetailView = (props: MetricDetailViewProps): React.ReactElement | null => {
  const data = props.data;
  if (!data) {
    return null;
  }

  // authoredWindow is tracked separately so that when the window switcher switches away from the authored window
  // it is still listed as an option
  const [authoredWindow, setAuthoredWindow] = React.useState<DateWindow | undefined>(
    props.definition && getFirstWindow(props.definition)
  );
  const [selectedWindow, setSelectedWindow] = React.useState<DateWindow | undefined>(
    props.definition && getFirstWindowOrDefault(props.definition)
  );

  // When the user starts editing the config, we want to restore the time series window to the authored window.
  // We also still want the window to change as the user edits the window while editing the config.
  React.useEffect(() => {
    // authoredWindow also needs to be updated so that the window switcher shows the correct options.
    const newAuthoredWindow = props.definition && getFirstWindowOrDefault(props.definition);
    setAuthoredWindow(newAuthoredWindow);
    setSelectedWindow(newAuthoredWindow);
  }, [props.definition]);

  const definitionWithDateWindow =
    props.definition && selectedWindow && definitionWithWindow(props.definition, selectedWindow);

  const windowSwitcherOptions = createWindowSwitcherOptions(
    dateTruncFromGranularity(getTimestampGranularity(props.definition)),
    authoredWindow
  );

  const measureFormat = definitionWithDateWindow && getMeasureFormatString(definitionWithDateWindow);
  const dataToRender = definitionWithDateWindow && materializeTable(definitionWithDateWindow, data).unwrapOr([]);

  // The window switcher should be visible if the metric has more than one data point, NOT when the data
  // with the window applied has more than one data point.
  const shouldShowWindowSwitcher = props.flags.metricsWindowSwitcherEnabled && data.length > 1;

  return (
    <InnerMetricDetailView
      data={dataToRender}
      // definition may be undefined if it comes undefined from VPC, but InnerMetricDetailView handles that
      definition={definitionWithDateWindow!}
      disabledWindowSwitcher={props.disabledWindowSwitcher}
      shouldShowWindowSwitcher={shouldShowWindowSwitcher}
      selectedDateWindow={selectedWindow}
      windowSwitcherOptions={windowSwitcherOptions}
      timeSeriesId={props.timeSeriesId}
      onWindowChange={setSelectedWindow}
      metricsReferenceLineEnabled={props.flags.metricsReferenceLineEnabled}
      metricColorIndicatorsEnabled={props.flags.metricsColorIndicatorsEnabled}
      measureFormat={measureFormat}
      lastUpdatedTimestamp={props.dataRefreshedTimestamp}
      testId={'metric-detail'}
      height={props.size?.height}
      width={props.size?.width}
    />
  );
};

export interface InnerMetricDetailViewProps extends MetricsBaseProps {
  readonly metricsReferenceLineEnabled?: boolean;
  readonly disabledWindowSwitcher?: boolean;
  readonly shouldShowWindowSwitcher?: boolean;
  readonly selectedDateWindow?: DateWindow;
  readonly windowSwitcherOptions: readonly DateWindow[];
  readonly timeSeriesId: string;
  readonly onWindowChange: (window: DateWindow) => void;
}

interface InnerMetricDetailViewState {
  readonly selectedValue?: DataRow;
  readonly updatedTimestamp?: Date;
  readonly stringifiedDefinition?: string;
}

export class InnerMetricDetailView extends React.PureComponent<InnerMetricDetailViewProps, InnerMetricDetailViewState> {
  public static defaultProps: Pick<InnerMetricDetailViewProps, 'timeSeriesId'> = {
    timeSeriesId: 'timeseries-container',
  };

  public constructor(props: Readonly<InnerMetricDetailViewProps>) {
    super(props);
    const initialValue = getBAN(this.props.data, this.props.lastUpdatedTimestamp);
    this.state = {
      selectedValue: initialValue,
      stringifiedDefinition: JSON.stringify(props.definition),
    };
  }

  private onActivated = (selectedValue: DataRow) => {
    this.setState((state) => {
      return {
        selectedValue,
      };
    });
  };

  public render(): JSX.Element | null {
    const measureAccessor = this.props.definition && buildMeasureAccessor(this.props.definition);
    if (!measureAccessor) {
      return null;
    }
    const timestampAccessor = this.props.definition && buildTimestampAccessor(this.props.definition);
    const comparisonMeasureAccessor =
      this.props.metricsReferenceLineEnabled && this.props.definition
        ? buildFirstThresholdMeasureAccessor(this.props.definition)
        : undefined;
    const comparisonTimestampAccessor =
      this.props.definition && buildFirstThresholdTimestampAccessor(this.props.definition);

    const colorAccessor = buildColorAccessor(this.props.definition);

    const banColor = this.props.metricColorIndicatorsEnabled ? MetricsGrey : MetricBlue;

    const dateFormatter = getDateFormatter(this.props.definition);

    const windowRange = this.getWindowRange();
    return (
      <>
        <div
          data-tb-test-id={this.props.testId}
          style={{
            ...this.props.style,
            height: this.props.height ?? DEFAULT_HEIGHT,
            width: this.props.width,
            display: 'flex',
            flexFlow: 'column',
          }}
        >
          <div style={{ flexGrow: 0 }}>
            <div
              data-tb-test-id={'metric-detail-value'}
              style={{ ...detailValueStyle, color: banColor }}
              aria-label={Messages.ariaLabelSelectedValue()}
            >
              {formatDataPointAsMetricNumber(measureAccessor, this.state.selectedValue, this.props.measureFormat)}
            </div>
            {this.renderComparisonDeltaAndTimestampArea(
              dateFormatter,
              measureAccessor,
              timestampAccessor,
              colorAccessor,
              comparisonMeasureAccessor,
              comparisonTimestampAccessor
            )}
          </div>
          {/* There's only not a selectedDateWindow when there is no definition */}
          {this.props.shouldShowWindowSwitcher && this.props.selectedDateWindow && (
            <WindowSwitcher
              selectedWindow={this.props.selectedDateWindow}
              options={this.props.windowSwitcherOptions}
              disabled={this.props.disabledWindowSwitcher}
              timeSeriesId={this.props.timeSeriesId}
              onWindowChange={this.props.onWindowChange}
            />
          )}
          <div style={timeSeriesContainerStyle} role={'tabpanel'} id={this.props.timeSeriesId}>
            {this.renderTimeSeries(
              dateFormatter,
              measureAccessor,
              colorAccessor,
              timestampAccessor,
              comparisonMeasureAccessor,
              windowRange
            )}
          </div>
          {this.renderLegend(dateFormatter, timestampAccessor)}
        </div>
      </>
    );
  }

  public static getDerivedStateFromProps(
    nextProps: Readonly<InnerMetricDetailViewProps>,
    prevState: InnerMetricDetailViewState
  ): InnerMetricDetailViewState | undefined {
    if (
      nextProps.lastUpdatedTimestamp?.getTime() !== prevState.updatedTimestamp?.getTime() ||
      JSON.stringify(nextProps.definition) !== prevState.stringifiedDefinition
    ) {
      const selectedValue = getBAN(nextProps.data, nextProps.lastUpdatedTimestamp);
      return {
        selectedValue,
        updatedTimestamp: nextProps.lastUpdatedTimestamp,
        stringifiedDefinition: JSON.stringify(nextProps.definition),
      };
    }
    return {};
  }

  private renderComparisonDeltaAndTimestampArea = (
    dateFormatter: DateFormatterCollection,
    measureAccessor: ValueAccessor<number>,
    timestampAccessor: ValueAccessor<Date | undefined>,
    colorAccessor: ValueAccessor<string>,
    comparisonMeasureAccessor?: ValueAccessor<number | undefined>,
    comparisonTimestampAccessor?: ValueAccessor<Date | undefined>
  ) => {
    const delta = this.comparisonDelta(measureAccessor, comparisonMeasureAccessor);
    const color = this.props.metricColorIndicatorsEnabled
      ? (delta && colorAccessor(delta?.comparedPoint)) ?? MetricsNeutralColor
      : MetricsGrey;

    const iconProps: Omit<SvgIconProps, 'name'> = {
      altText: '',
      testId: 'metric-detail-delta-label',
      color,
      style: statusSymbolStyle,
      size: 20,
    };

    const colorIndicatorsFlag = {
      metricsColorIndicatorsEnabled: this.props.metricColorIndicatorsEnabled,
    };
    const status = getStatus(this.props.definition, colorIndicatorsFlag, this.state.selectedValue);

    const { text, accessibilityText } =
      !delta || !this.props.definition
        ? { text: NON_BREAKING_SPACE, accessibilityText: '' }
        : formatComparisonTextForDetail(delta, this.props.definition, this.props.measureFormat, status);

    const symbol = getIconForLabel(iconProps, status, this.props.definition, delta);

    return (
      <>
        <div
          data-tb-test-id={'measure-delta'}
          style={{ ...measureDeltaStyle, color }}
          role={'img'}
          aria-label={accessibilityText}
        >
          {symbol}
          {text}
        </div>
        <div
          data-tb-test-id={'selected-point-timestamp'}
          style={selectedTimeStyle}
          aria-label={Messages.ariaLabelSelectedTime()}
        >
          {this.selectedTimestampOrTimeDelta(dateFormatter, delta, timestampAccessor, comparisonTimestampAccessor)}
        </div>
      </>
    );
  };

  private comparisonDelta(
    measureAccessor?: ValueAccessor<number>,
    comparisonMeasureAccessor?: ValueAccessor<number | undefined>
  ): ComparisonDelta | undefined {
    const latestValue = this.state.selectedValue;
    return latestValue ? computeMetricComparison(latestValue, measureAccessor, comparisonMeasureAccessor) : undefined;
  }

  private getWindowRange(): DateRange | undefined {
    const dateWindow = getFirstWindow(this.props.definition);
    return dateWindow && this.props.data ? getWindowDateRange(dateWindow, this.props.data) : undefined;
  }

  private selectedTimestampOrTimeDelta(
    dateFormatter: DateFormatterCollection,
    delta?: ComparisonDelta,
    timestampAccessor?: ValueAccessor<Date | undefined>,
    comparisonTimestampAccessor?: ValueAccessor<Date | undefined>
  ): string {
    const timestamp = this.state.selectedValue && timestampAccessor && timestampAccessor(this.state.selectedValue);
    const comparisonTimestamp =
      delta && comparisonTimestampAccessor && comparisonTimestampAccessor(delta.comparedPoint);

    if (!comparisonTimestamp || !timestamp) {
      return timestamp ? dateFormatter.banTimestamp(timestamp) : '';
    }
    return dateFormatter.comparisonWidget(timestamp, comparisonTimestamp);
  }

  private shouldRenderReferenceLine = (): boolean => {
    return this.props.metricsReferenceLineEnabled ? shouldRenderFirstThresholdLine(this.props.definition) : false;
  };

  private renderTimeSeries = (
    dateFormatter: DateFormatterCollection,
    measureAccessor: ValueAccessor<number>,
    colorAccessor: ValueAccessor<string>,
    timestampAccessor?: ValueAccessor<Date | undefined>,
    comparisonMeasureAccessor?: ValueAccessor<number | undefined>,
    windowRange?: DateRange
  ) => {
    if (!isSingleNumberMetric(this.props.data) && timestampAccessor) {
      const lineStyle = getLineStyleForDefaultThresholdLine(this.props.definition);
      const comparisonLineInteractiveDot = !lineStyle?.hidden && lineStyle?.interactive;
      return (
        <ResizableDetailTimeSeries
          testId="time-series"
          data={this.props.data as ReadonlyArray<DataRow>}
          dateFormatter={dateFormatter}
          onActivated={this.onActivated}
          formatString={this.props.measureFormat}
          timestampAccessor={timestampAccessor}
          measureAccessor={measureAccessor}
          comparisonAccessor={comparisonMeasureAccessor}
          shouldRenderReferenceLine={this.shouldRenderReferenceLine()}
          comparisonLineStyle={getStylePropsForDefaultThresholdLine(this.props.definition)}
          comparisonLineLabelAccessor={buildComparisonLineLabelAccessor(
            this.props.definition,
            this.props.data as DataTable
          )}
          comparisonLineInteractiveDot={comparisonLineInteractiveDot}
          colorIndicatorsEnabled={this.props.metricColorIndicatorsEnabled ?? false}
          colorAccessor={colorAccessor}
          windowRange={windowRange}
          stringifiedDefinitionKey={JSON.stringify(this.props.definition)}
        />
      );
    } else {
      return <EmptyTimeSeries />;
    }
  };

  private renderLegend = (
    dateFormatter: DateFormatterCollection,
    timestampAccessor?: ValueAccessor<Date | undefined>
  ) => {
    if (!isSingleNumberMetric(this.props.data) && timestampAccessor) {
      const dateRange = this.getWindowRange() ?? getDateRange(this.props.data as DataTable);
      const showComparisonLine = this.shouldRenderReferenceLine();
      const comparisonRange = showComparisonLine ? getComparisonDateRange(this.props.data as DataTable) : undefined;
      return (
        dateRange && (
          <MetricLegend
            currentRange={dateRange}
            comparisonRange={comparisonRange}
            dateFormatter={dateFormatter}
            metricColorIndicatorsEnabled={this.props.metricColorIndicatorsEnabled}
          />
        )
      );
    } else {
      return null;
    }
  };
}

const detailValueStyle: React.CSSProperties = {
  marginTop: '12px',
  fontWeight: 300,
  fontSize: '48px',
  lineHeight: '1.2',
};

const selectedTimeStyle: React.CSSProperties = {
  color: Colors!.L9,
  fontSize: '14px',
  marginBottom: '12px',
  marginTop: '8px',
};

const measureDeltaStyle: React.CSSProperties = {
  color: Colors!.L9,
  display: 'flex',
  fontSize: '20px',
  marginBottom: '12px',
  marginTop: '8px',
  lineHeight: '1.2',
  alignItems: 'center',
};

const timeSeriesContainerStyle: React.CSSProperties = {
  flex: '1 1',
  minHeight: '150px',
};

const statusSymbolStyle: React.CSSProperties = {
  marginRight: '10px',
  marginBottom: '2px',
};

const NON_BREAKING_SPACE = '\xa0';
